import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

type UserType = 'candidate' | 'company' | null;
type UserContext = 'candidate' | 'company' | null;

interface UseUserTypeResult {
  userType: UserType;
  loading: boolean;
  userId: string | null;
}

const getUserContext = (): UserContext => {
  const context = localStorage.getItem('userContext');
  if (context === 'candidate' || context === 'company') {
    return context;
  }
  return null;
};

export const useUserType = (): UseUserTypeResult => {
  const [userType, setUserType] = useState<UserType>(null);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const checkUserType = async () => {
      try {
        const { data: { user }, error } = await supabase.auth.getUser();
        
        if (!isMounted) return;

        if (error || !user) {
          setUserType(null);
          setUserId(null);
          setLoading(false);
          return;
        }

        setUserId(user.id);
        const userContext = getUserContext();

        // RESPEITAR o contexto definido no login - NÃO fazer fallback
        if (userContext === 'company') {
          // Verificar se é owner
          const { data: companyData } = await supabase
            .from('company_profiles')
            .select('id')
            .eq('user_id', user.id)
            .maybeSingle();

          if (!isMounted) return;

          if (companyData) {
            setUserType('company');
            setLoading(false);
            return;
          }

          // NÃO fazer fallback para candidate - contexto é 'company' mas não encontrou perfil
          setUserType(null);
          setLoading(false);
          return;
        }
        
        if (userContext === 'candidate') {
          // Verificar se tem perfil de candidato
          const { data: candidateData } = await supabase
            .from('candidate_profiles')
            .select('id')
            .eq('user_id', user.id)
            .maybeSingle();

          if (!isMounted) return;

          if (candidateData) {
            setUserType('candidate');
            setLoading(false);
            return;
          }

          // NÃO fazer fallback para company - contexto é 'candidate' mas não encontrou perfil
          setUserType(null);
          setLoading(false);
          return;
        }

        // Sem contexto definido - verificar na ordem padrão (candidato primeiro)
        const { data: candidateData } = await supabase
          .from('candidate_profiles')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!isMounted) return;

        if (candidateData) {
          setUserType('candidate');
          setLoading(false);
          return;
        }

        const { data: companyData } = await supabase
          .from('company_profiles')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!isMounted) return;

        if (companyData) {
          setUserType('company');
          setLoading(false);
          return;
        }

        setUserType(null);
        setLoading(false);
      } catch (error) {
        console.error('Error checking user type:', error);
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    checkUserType();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isMounted) return;

      if (event === 'SIGNED_OUT') {
        setUserType(null);
        setUserId(null);
        localStorage.removeItem('userContext');
        setLoading(false);
        return;
      }

      if (session?.user) {
        setUserId(session.user.id);
        setTimeout(() => {
          if (isMounted) {
            checkUserType();
          }
        }, 0);
      } else {
        setUserType(null);
        setUserId(null);
        setLoading(false);
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  return { userType, loading, userId };
};
